<template>
  <div class="conflicts-page">
    <div class="page-wrapper">
      <header class="page-header">
        <div class="header-text">
          <h1><i class="mdi mdi-account-alert"></i> Students With Conflicts</h1>
          <p>One row per student, so a repeat offender is resolved once instead of ten times.</p>
        </div>
        <div class="header-actions">
          <router-link to="/conflicts" class="btn ghost">
            <i class="mdi mdi-format-list-bulleted"></i> By incident
          </router-link>
          <router-link to="/conflicts/scans" class="btn ghost">
            <i class="mdi mdi-face-recognition"></i> Successful scans
          </router-link>
          <button class="btn ghost" :disabled="loading" @click="refresh">
            <i class="mdi" :class="loading ? 'mdi-loading mdi-spin' : 'mdi-refresh'"></i>
            Refresh
          </button>
        </div>
      </header>

      <div v-if="toast" class="toast" :class="toast.kind">
        <i class="mdi" :class="toast.kind === 'error' ? 'mdi-alert-circle' : 'mdi-check-circle'"></i>
        <span>{{ toast.text }}</span>
        <button class="toast-close" @click="toast = null"><i class="mdi mdi-close"></i></button>
      </div>

      <section class="filters">
        <div class="status-tabs">
          <button
            v-for="tab in statusTabs"
            :key="tab.value"
            class="tab"
            :class="{ active: status === tab.value }"
            @click="setStatus(tab.value)"
          >
            {{ tab.label }}
          </button>
        </div>

        <div class="search-box">
          <i class="mdi mdi-magnify"></i>
          <input v-model="q" type="text" placeholder="Search student or ID…" @input="debouncedRefresh" />
        </div>
      </section>

      <section class="table-card">
        <div v-if="loading" class="empty-state">
          <i class="mdi mdi-loading mdi-spin"></i>
          <p>Loading…</p>
        </div>

        <div v-else-if="students.length === 0" class="empty-state">
          <i class="mdi mdi-check-circle-outline"></i>
          <p>No students have conflicts with this filter.</p>
        </div>

        <table v-else class="conflict-table">
          <thead>
            <tr>
              <th>Student</th>
              <th>EdClock ID</th>
              <th>Conflicts</th>
              <th>Breakdown</th>
              <th>Last seen</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in students"
              :key="row.biometricsStudentId || row.studentName || 'unknown'"
              class="clickable"
              @click="openResolve(row)"
            >
              <td class="col-student">
                <div class="student-name">
                  {{ row.studentName || 'Unidentified' }}
                  <span v-if="!row.enrolled" class="chip gone">no record</span>
                </div>
                <div class="student-id">{{ row.biometricsStudentId || '—' }}</div>
              </td>
              <td>
                <span v-if="row.edClockStudentId" class="mono">{{ row.edClockStudentId }}</span>
                <span v-else class="chip danger">not linked</span>
              </td>
              <td>
                <span class="count-pill" :class="{ hot: row.openCount >= 3 }">{{ row.openCount }} open</span>
                <span class="count-sub">{{ row.total }} total</span>
              </td>
              <td class="col-breakdown">
                <span
                  v-for="(count, type) in row.typeCounts"
                  :key="type"
                  class="type-badge"
                  :class="type.toLowerCase()"
                >
                  {{ typeLabel(type) }} × {{ count }}
                </span>
              </td>
              <td class="col-when">{{ formatTime(row.lastOccurredAt) }}</td>
              <td class="col-actions">
                <button
                  class="btn small ghost"
                  :disabled="row.openCount === 0"
                  @click.stop="openResolve(row)"
                >
                  Resolve all
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </section>
    </div>

    <div v-if="target" class="drawer-backdrop" @click.self="target = null">
      <aside class="drawer">
        <header class="drawer-header">
          <div>
            <h2>{{ target.studentName || 'Unidentified' }}</h2>
            <p class="drawer-time">
              Closing {{ target.openCount }} open conflict(s) in one action.
            </p>
          </div>
          <button class="icon-btn" @click="target = null"><i class="mdi mdi-close"></i></button>
        </header>

        <div class="drawer-body">
          <dl class="detail-grid">
            <div>
              <dt>Biometrics ID</dt>
              <dd>{{ target.biometricsStudentId || '—' }}</dd>
            </div>
            <div>
              <dt>EdClock ID</dt>
              <dd :class="{ missing: !target.edClockStudentId }">
                {{ target.edClockStudentId || 'not linked' }}
              </dd>
            </div>
          </dl>

          <div class="incidents">
            <h3>Incidents</h3>
            <p v-if="incidentsLoading" class="hint">Loading incidents…</p>
            <p v-else-if="incidents.length === 0" class="hint">No incidents found for this student.</p>
            <ul v-else class="incident-list">
              <li v-for="incident in incidents" :key="incident._id">
                <div class="incident-head">
                  <span class="type-badge" :class="incident.type.toLowerCase()">
                    {{ typeLabel(incident.type) }}
                  </span>
                  <span class="status-badge" :class="incident.status">{{ incident.status }}</span>
                </div>
                <p class="incident-message">{{ incident.message }}</p>
                <div class="incident-meta">{{ formatTime(incident.occurredAt) }}</div>
              </li>
            </ul>
          </div>

          <div v-if="!target.edClockStudentId && target.enrolled" class="link-block">
            <label>Link to the correct EdClock student</label>
            <div class="search-box">
              <i class="mdi mdi-magnify"></i>
              <input
                v-model="edclockQuery"
                type="text"
                placeholder="Search EdClock by name…"
                @input="debouncedEdClockSearch"
              />
              <i v-if="edclockSearching" class="mdi mdi-loading mdi-spin"></i>
            </div>

            <ul v-if="edclockResults.length" class="edclock-results">
              <li v-for="student in edclockResults" :key="student.id">
                <div>
                  <span class="result-name">{{ student.name }}</span>
                  <span class="result-id">ID {{ student.id }}</span>
                </div>
                <button class="btn small primary" :disabled="resolving" @click="bulk('link_edclock_id', student.id)">
                  Link
                </button>
              </li>
            </ul>
            <p v-else-if="edclockQuery.length >= 2 && !edclockSearching" class="hint">
              No EdClock students found.
            </p>
          </div>

          <label class="note-label">Note (optional)</label>
          <textarea v-model="note" rows="2" placeholder="Why was this resolved this way?"></textarea>

          <div v-if="target.openCount > 0 && target.biometricsStudentId" class="resolve-actions">
            <button class="btn danger" :disabled="resolving" @click="bulk('marked_false_match')">
              <i class="mdi mdi-account-alert"></i> False match
            </button>
            <button class="btn success" :disabled="resolving" @click="bulk('confirmed_identity')">
              <i class="mdi mdi-account-check"></i> Identity confirmed
            </button>
            <button class="btn ghost" :disabled="resolving" @click="bulk('ignored')">
              <i class="mdi mdi-eye-off"></i> Ignore
            </button>
          </div>

          <p class="hint footnote">
            Per-incident detail, including match candidates, stays on the
            <router-link to="/conflicts">by-incident view</router-link>.
          </p>
        </div>
      </aside>
    </div>
  </div>
</template>

<script>
import moment from 'moment';

const TYPE_LABELS = {
  AMBIGUOUS_FACE_MATCH: 'Ambiguous',
  LOW_CONFIDENCE_MATCH: 'Low confidence',
  UNLINKED_EDCLOCK_ID: 'Unlinked',
  ORPHANED_FACE_INDEX: 'Orphaned index',
  DUPLICATE_EDCLOCK_LINK: 'Duplicate link',
};

export default {
  name: 'ConflictStudentsView',
  data() {
    return {
      students: [],
      status: 'open',
      q: '',
      statusTabs: [
        { value: 'open', label: 'Open' },
        { value: 'resolved', label: 'Resolved' },
        { value: 'all', label: 'All' },
      ],
      loading: false,
      resolving: false,
      target: null,
      incidents: [],
      incidentsLoading: false,
      note: '',
      edclockQuery: '',
      edclockResults: [],
      edclockSearching: false,
      toast: null,
      refreshTimer: null,
      searchTimer: null,
    };
  },
  created() {
    this.refresh();
  },
  beforeUnmount() {
    clearTimeout(this.refreshTimer);
    clearTimeout(this.searchTimer);
  },
  methods: {
    apiUrl(path) {
      const base = String(process.env.VUE_APP_API_URL || '').replace(/\/+$/, '');
      return `${base}${path}`;
    },
    typeLabel(type) {
      return TYPE_LABELS[type] || type;
    },
    formatTime(value) {
      return value ? moment(value).format('MMM D, YYYY · h:mm A') : '—';
    },
    showToast(text, kind = 'success') {
      this.toast = { text, kind };
      setTimeout(() => {
        this.toast = null;
      }, 5000);
    },
    setStatus(value) {
      this.status = value;
      this.refresh();
    },
    debouncedRefresh() {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = setTimeout(this.refresh, 350);
    },

    async refresh() {
      this.loading = true;
      try {
        const params = new URLSearchParams({ status: this.status });
        if (this.q) params.set('q', this.q);

        const response = await fetch(this.apiUrl(`/api/conflicts/students?${params.toString()}`));
        if (!response.ok) throw new Error('Failed to load students');

        const data = await response.json();
        this.students = data.students || [];
      } catch (error) {
        this.showToast(error.message || 'Failed to load students', 'error');
      } finally {
        this.loading = false;
      }
    },

    async openResolve(row) {
      this.target = row;
      this.note = '';
      this.incidents = [];
      this.edclockResults = [];
      this.edclockQuery = row.studentName || '';
      if (!row.edClockStudentId && this.edclockQuery.length >= 2) {
        this.searchEdClock();
      }
      await this.loadIncidents(row);
    },

    async loadIncidents(row) {
      if (!row.biometricsStudentId) {
        this.incidents = [];
        return;
      }

      this.incidentsLoading = true;
      try {
        const params = new URLSearchParams({ status: this.status });
        const response = await fetch(
          this.apiUrl(
            `/api/conflicts/students/${encodeURIComponent(row.biometricsStudentId)}?${params.toString()}`
          )
        );
        if (!response.ok) throw new Error('Failed to load incidents');
        const data = await response.json();
        this.incidents = data.conflicts || [];
      } catch (error) {
        this.incidents = [];
        this.showToast(error.message || 'Failed to load incidents', 'error');
      } finally {
        this.incidentsLoading = false;
      }
    },

    debouncedEdClockSearch() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(this.searchEdClock, 350);
    },
    async searchEdClock() {
      const q = this.edclockQuery.trim();
      if (q.length < 2) {
        this.edclockResults = [];
        return;
      }

      this.edclockSearching = true;
      try {
        const response = await fetch(
          this.apiUrl(`/api/conflicts/edclock-search?q=${encodeURIComponent(q)}`)
        );
        if (!response.ok) throw new Error('EdClock search failed');
        const data = await response.json();
        this.edclockResults = data.students || [];
      } catch (error) {
        this.edclockResults = [];
        this.showToast(error.message || 'EdClock search failed', 'error');
      } finally {
        this.edclockSearching = false;
      }
    },

    async bulk(action, edClockStudentId) {
      if (!this.target || !this.target.biometricsStudentId) return;

      this.resolving = true;
      try {
        const response = await fetch(
          this.apiUrl(
            `/api/conflicts/students/${encodeURIComponent(this.target.biometricsStudentId)}/resolve`
          ),
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ action, edClockStudentId, note: this.note }),
          }
        );

        const data = await response.json();
        if (!response.ok) throw new Error(data.error || 'Failed to resolve');

        this.showToast(
          action === 'link_edclock_id'
            ? `Linked to EdClock ID ${edClockStudentId} and closed ${data.resolved} conflict(s).`
            : `Closed ${data.resolved} conflict(s).`
        );
        this.target = null;
        await this.refresh();
      } catch (error) {
        this.showToast(error.message || 'Failed to resolve', 'error');
      } finally {
        this.resolving = false;
      }
    },
  },
};
</script>

<style scoped>
.conflicts-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%);
  padding: 2rem 1rem 4rem;
}

.page-wrapper {
  max-width: 1240px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 1.5rem;
}

.header-text h1 {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 1.9rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 0.35rem;
}

.header-text h1 i {
  color: #6366f1;
}

.header-text p {
  margin: 0;
  color: #64748b;
  font-size: 0.95rem;
}

.header-actions {
  display: flex;
  gap: 0.6rem;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.6rem 1.1rem;
  border-radius: 10px;
  border: 1px solid transparent;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  text-decoration: none;
}

.btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.btn.primary {
  background: #6366f1;
  color: #fff;
}

.btn.primary:hover:not(:disabled) {
  background: #4f46e5;
}

.btn.ghost {
  background: #fff;
  border-color: #e2e8f0;
  color: #475569;
}

.btn.ghost:hover:not(:disabled) {
  background: #f1f5f9;
}

.btn.danger {
  background: #fee2e2;
  color: #b91c1c;
}

.btn.success {
  background: #dcfce7;
  color: #15803d;
}

.btn.small {
  padding: 0.35rem 0.75rem;
  font-size: 0.8rem;
}

.icon-btn {
  border: none;
  background: transparent;
  color: #94a3b8;
  font-size: 1.4rem;
  cursor: pointer;
  line-height: 1;
}

.toast {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.85rem 1rem;
  border-radius: 12px;
  margin-bottom: 1.25rem;
  font-size: 0.9rem;
  font-weight: 500;
}

.toast.success {
  background: #dcfce7;
  color: #166534;
}

.toast.error {
  background: #fee2e2;
  color: #991b1b;
}

.toast-close {
  margin-left: auto;
  border: none;
  background: transparent;
  cursor: pointer;
  color: inherit;
}

.filters {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 1rem;
}

.status-tabs {
  display: flex;
  background: #fff;
  border-radius: 10px;
  padding: 0.25rem;
  box-shadow: 0 1px 3px rgb(0 0 0 / 0.06);
}

.tab {
  border: none;
  background: transparent;
  padding: 0.5rem 1.1rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
}

.tab.active {
  background: #6366f1;
  color: #fff;
}

.search-box {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 260px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 0.55rem 0.85rem;
}

.search-box i {
  color: #94a3b8;
}

.search-box input {
  border: none;
  outline: none;
  flex: 1;
  font-size: 0.875rem;
  color: #334155;
  background: transparent;
}

.table-card {
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgb(0 0 0 / 0.08);
}

.conflict-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.conflict-table th {
  text-align: left;
  padding: 0.85rem 1rem;
  background: #f8fafc;
  color: #64748b;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid #e2e8f0;
}

.conflict-table td {
  padding: 0.85rem 1rem;
  border-bottom: 1px solid #f1f5f9;
  vertical-align: top;
}

.conflict-table tbody tr.clickable {
  cursor: pointer;
}

.conflict-table tbody tr:hover {
  background: #f8fafc;
}

.status-badge {
  display: inline-block;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: capitalize;
}

.status-badge.open {
  background: #fee2e2;
  color: #991b1b;
}

.status-badge.resolved {
  background: #dcfce7;
  color: #166534;
}

.status-badge.ignored {
  background: #e2e8f0;
  color: #475569;
}

.incidents h3 {
  font-size: 0.95rem;
  color: #1e293b;
  margin: 0 0 0.75rem;
}

.incident-list {
  list-style: none;
  padding: 0;
  margin: 0 0 1.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  max-height: 280px;
  overflow-y: auto;
}

.incident-list li {
  padding: 0.75rem 0.9rem;
  border-bottom: 1px solid #f1f5f9;
}

.incident-list li:last-child {
  border-bottom: none;
}

.incident-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.35rem;
}

.incident-message {
  margin: 0;
  font-size: 0.82rem;
  color: #475569;
  line-height: 1.45;
}

.incident-meta {
  margin-top: 0.3rem;
  font-size: 0.72rem;
  color: #94a3b8;
}

.student-name {
  font-weight: 600;
  color: #1e293b;
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.student-id,
.mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.75rem;
  color: #94a3b8;
}

.mono {
  font-size: 0.85rem;
  color: #1e293b;
}

.chip {
  display: inline-block;
  padding: 0.12rem 0.5rem;
  border-radius: 999px;
  font-size: 0.68rem;
  font-weight: 700;
}

.chip.danger {
  background: #fee2e2;
  color: #b91c1c;
}

.chip.gone {
  background: #fef3c7;
  color: #92400e;
}

.count-pill {
  display: inline-block;
  padding: 0.18rem 0.6rem;
  border-radius: 999px;
  background: #e0e7ff;
  color: #3730a3;
  font-size: 0.75rem;
  font-weight: 700;
}

.count-pill.hot {
  background: #fee2e2;
  color: #991b1b;
}

.count-sub {
  display: block;
  font-size: 0.7rem;
  color: #94a3b8;
  margin-top: 0.2rem;
}

.col-breakdown {
  max-width: 320px;
}

.type-badge {
  display: inline-block;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  margin: 0 0.25rem 0.25rem 0;
  background: #e2e8f0;
  color: #475569;
}

.type-badge.ambiguous_face_match {
  background: #fef3c7;
  color: #92400e;
}

.type-badge.low_confidence_match {
  background: #fee2e2;
  color: #991b1b;
}

.type-badge.unlinked_edclock_id {
  background: #e0e7ff;
  color: #3730a3;
}

.type-badge.orphaned_face_index {
  background: #fce7f3;
  color: #9d174d;
}

.type-badge.duplicate_edclock_link {
  background: #ccfbf1;
  color: #115e59;
}

.col-when {
  color: #64748b;
  white-space: nowrap;
}

.col-actions {
  text-align: right;
}

.empty-state {
  padding: 4rem 1rem;
  text-align: center;
  color: #94a3b8;
}

.empty-state i {
  font-size: 2.5rem;
  opacity: 0.6;
}

.empty-state p {
  margin: 0.75rem 0 0;
  font-size: 0.9rem;
  font-weight: 500;
}

.drawer-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  justify-content: flex-end;
  z-index: 9999;
}

.drawer {
  width: min(480px, 100%);
  background: #fff;
  height: 100%;
  overflow-y: auto;
  box-shadow: -12px 0 40px rgba(15, 23, 42, 0.15);
}

.drawer-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.5rem;
  border-bottom: 1px solid #f1f5f9;
}

.drawer-header h2 {
  font-size: 1.3rem;
  color: #1e293b;
  margin: 0 0 0.25rem;
}

.drawer-time {
  margin: 0;
  font-size: 0.82rem;
  color: #94a3b8;
}

.drawer-body {
  padding: 1.5rem;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  margin: 0 0 1.5rem;
}

.detail-grid dt {
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #94a3b8;
  margin-bottom: 0.2rem;
}

.detail-grid dd {
  margin: 0;
  font-size: 0.88rem;
  color: #1e293b;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  word-break: break-all;
}

.detail-grid dd.missing {
  color: #dc2626;
  font-weight: 600;
}

.link-block {
  margin-bottom: 1.25rem;
}

.link-block label,
.note-label {
  display: block;
  font-size: 0.78rem;
  font-weight: 600;
  color: #475569;
  margin-bottom: 0.4rem;
}

.edclock-results {
  list-style: none;
  padding: 0;
  margin: 0.6rem 0 0;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  max-height: 220px;
  overflow-y: auto;
}

.edclock-results li {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.6rem 0.85rem;
  border-bottom: 1px solid #f1f5f9;
}

.edclock-results li:last-child {
  border-bottom: none;
}

.result-name {
  display: block;
  font-size: 0.86rem;
  font-weight: 600;
  color: #1e293b;
}

.result-id {
  font-size: 0.72rem;
  color: #94a3b8;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}

.hint {
  font-size: 0.8rem;
  color: #94a3b8;
  margin: 0.5rem 0 0;
}

.footnote {
  margin-top: 1.25rem;
}

textarea {
  width: 100%;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 0.6rem 0.75rem;
  font-size: 0.86rem;
  font-family: inherit;
  color: #334155;
  resize: vertical;
  outline: none;
}

.resolve-actions {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-top: 1rem;
}

.mdi-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 900px) {
  .col-breakdown,
  .conflict-table th:nth-child(4) {
    display: none;
  }

  .detail-grid {
    grid-template-columns: 1fr;
  }
}
</style>
